import type { TranslateFn } from '../../../utils/filterOperators';

export interface DiagnosticSentenceContext {
  /** Members reporting / in the population, for the coverage-floor sentence. */
  reporting?: number;
  total?: number;
  /** The coverage floor as a percentage. */
  minCoverage?: number;
  /** The status the floor substituted, already localised. */
  floorStatusLabel?: string;
  /** The weight floor, formatted as the editor shows it. */
  influenceFloor?: string;
  contributionUnavailableCount?: number;
}

export interface DiagnosticSentence {
  code: string;
  text: string;
}

const warned = new Set<string>();

const present = (...values: unknown[]) => values.every((value) => value !== undefined && value !== null);

/**
 * Turns the engine's diagnostic codes into sentences that say what happened and what to do. A code this client
 * doesn't know is logged once and dropped: an unrecognised message must never reach the page as raw text.
 */
export function diagnosticSentences(
  codes: readonly string[] | null | undefined,
  context: DiagnosticSentenceContext,
  t: TranslateFn
): DiagnosticSentence[] {
  const sentences: DiagnosticSentence[] = [];
  for (const code of new Set(codes ?? [])) {
    const text = sentenceFor(code, context, t);
    if (text === null) {
      if (!warned.has(code)) {
        warned.add(code);
        console.warn(`Unknown rollup diagnostic code "${code}" dropped`);
      }
      continue;
    }
    sentences.push({ code, text });
  }
  return sentences;
}

function sentenceFor(code: string, context: DiagnosticSentenceContext, t: TranslateFn): string | null {
  switch (code) {
    case 'below_min_coverage':
      return present(context.reporting, context.total, context.minCoverage, context.floorStatusLabel)
        ? t('statusFactors.diagnostics.belowMinCoverage', {
            reporting: context.reporting,
            total: context.total,
            minCoverage: context.minCoverage,
            status: context.floorStatusLabel,
          })
        : t('statusFactors.diagnostics.belowMinCoverageGeneric');
    case 'influence_floor_excluded_all':
      return present(context.influenceFloor)
        ? t('statusFactors.diagnostics.influenceFloorExcludedAll', { influenceFloor: context.influenceFloor })
        : t('statusFactors.diagnostics.influenceFloorExcludedAllGeneric');
    case 'zero_total_influence':
      return t('statusFactors.diagnostics.zeroTotalInfluence');
    case 'contribution_unavailable':
      return t('statusFactors.diagnostics.contributionUnavailable', {
        count: context.contributionUnavailableCount ?? 1,
      });
    default:
      return null;
  }
}
